import { filter, take } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalsService } from '../../../shared/modals/modals.service';
import { LabelValueEditModalComponent } from '../label-value-edit-modal/label-value-edit-modal.component';
import { camelCase, get } from 'lodash-es';
import { LabelValueOptions, ValueAutoFillTransformTypes } from '../label-value-interface';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BidiService } from '../../../core/i18n/bidi.service';
@Component({
  standalone: false,
  selector: 'gd-label-value-list-form',
  templateUrl: './label-value-list-form.component.html',
  styleUrls: ['./label-value-list-form.component.scss'],
})

export class LabelValueListFormComponent implements OnInit, OnDestroy {
  fieldLabelSubscription: Subscription = new Subscription();
  createForm: UntypedFormGroup = this.fb.group({
    label: ['', Validators.required],
    value: ['', Validators.required],
  });

  listData = [];
  displayedColumns = ['label', 'value', 'controls'];

  @Input() hasActionPermission = true;
  @Input() editingMode = true;
  @Input() options: LabelValueOptions = {
    valueAutoFill: false,
    valueAutoFillTransform: ValueAutoFillTransformTypes.CamelCase,
    inputType: ''
  };

  /**
   * This input can be used to directly bind list to a formControl
   */
  @Input() listFormControl: UntypedFormControl = null;

  @Output() listChanged = new EventEmitter();

  @Input() uiText = {
    itemAlreadyExists: $localize`Similar item already exists.Duplicate label or value.`,
    editModalTitle: $localize`Edit list item`,
    confirmDelete: $localize`Are you sure you want to delete this item?`,
    deleteModalTitle: $localize`Delete list item`,
    keyLabel: $localize`Label`,
    valueLabel: $localize`Value`
  };

  @Input() formClasses: string[] = [];

  get keyLabel() {
    return get(this.uiText, 'keyLabel', 'Label');
  }

  get valueLabel() {
    return get(this.uiText, 'valueLabel', 'Value');
  }

  get fieldLabelControl() {
    return <UntypedFormControl>this.createForm.get('label');
  }

  get fieldValueControl() {
    return <UntypedFormControl>this.createForm.get('value');
  }
  dir$ =  this.bidiService.getEffectiveLocaleDirectionality();

  constructor(
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private modalsService: ModalsService,
    private bidiService: BidiService,
  ) { }

  ngOnInit() {

    if(!this.hasActionPermission) {
      this.createForm.disable();
    }

    if (this.options.valueAutoFill) {
      this.fieldLabelSubscription.add(this.fieldLabelControl.valueChanges.subscribe((label) => {
        this.fieldValueControl.setValue(this.setAutoFillFormatting(label));
        return !this.fieldValueControl.touched && this.fieldValueControl.markAsTouched();
      }));
    }

    if (this.listFormControl) {
      this.fieldLabelSubscription.add(
        this.listFormControl.valueChanges
          .pipe(filter(e => !!e), take(1))
          .subscribe(val => this.listData = val)
      );
    }
  }

  ngOnDestroy() {
    this.fieldLabelSubscription.unsubscribe();
  }

  setAutoFillFormatting(label) {
    switch (this.options.valueAutoFillTransform) {
      case ValueAutoFillTransformTypes.CamelCase:
        return camelCase(label);
    }
  }

  setList(newListData, options = { markAsDirty: false, emitEvent: false }) {
    this.updateListValue(newListData, options);
  }

  clearList() {
    this.createForm.reset();
    this.listData = [];
    if (this.listFormControl) {
      this.listFormControl.setValue([]);
      this.listFormControl.markAsPristine();
    }
  }

  addItem() {
    if (this.createForm.invalid) {
      return this.createForm.markAllAsTouched();
    }

    const item = this.createForm.value;
    const alreadyExist = !!this.listData.find(
      (li) => li.label === item.label || li.value === item.value
    );

    // TODO add an option to only validate against duplicate keys
    if (alreadyExist) {
      return this.snackBar.open(this.uiText.itemAlreadyExists, $localize`Close`, { duration: 4000 });
    }

    const newListData = [...(this.listData || []), item];
    this.updateListValue(newListData);

    this.createForm.reset();
    this.createForm.markAsPristine();
  }

  editItem(index) {
    const item = this.listData[+index];
    const usedItems = this.listData.filter(
      (li) => item.value !== li.value && item.label !== li.label
    );
    this.modalsService
      .custom(this.uiText.editModalTitle, LabelValueEditModalComponent, {
        data: { item, usedItems, uiText: this.uiText },
        width: '400px',
      })
      .pipe(filter((response) => !!response))
      .subscribe((response) => {
        const newListData = [
          ...this.listData.slice(0, +index),
          response.item,
          ...this.listData.slice(+index + 1),
        ];
        this.updateListValue(newListData);
      });
  }

  removeItem(index) {
    this.modalsService
      .confirm(this.uiText.deleteModalTitle, this.uiText.confirmDelete)
      .pipe(filter((r) => !!r))
      .subscribe(() => {
        const newListData = [...this.listData.slice(0, +index), ...this.listData.slice(+index + 1)];
        this.updateListValue(newListData);
      });
  }

  copyItemValue(index) {
    const item = this.listData[+index];
    navigator.clipboard.writeText(item.value);
    this.snackBar.open($localize`Copied to clipboard`, $localize`Close`, { duration: 3000 });
  }

  private updateListValue(newValue, options = { markAsDirty: true, emitEvent: true }) {
    this.listData = newValue;

    if (options.emitEvent) {
      this.listChanged.emit(this.listData);
    }

    if (this.listFormControl) {
      this.listFormControl.setValue(this.listData);
      if (options.markAsDirty) {
        this.listFormControl.markAsDirty();
      }
    }
  }
  handleDropEvent(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.listData, event.previousIndex, event.currentIndex);
    // trigger onchange detection
    this.listData = [...this.listData];
    this.updateListValue(this.listData);
  }
}
